import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';

import { AlertPopup } from 'app/app.config';
import { ELibraryService } from '../e-library/e-library.service';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

export interface ExcelColumn {
  field: string;
  header: string;
}

// export interface ExcelRow {
//   _id: number;
//   /** Marks this row as the document header/footer/resources row instead of a normal data row */
//   _special?: 'header' | 'footer' | 'resources';
//   [key: string]: any;
// }

interface ResourceItem {
  title: string;
  url: string;
}

interface ExcelRow {
  _id: number;
  _special?: 'header' | 'footer' | 'resources';
  title: string;
  content: string | ResourceItem[];
}

/**
 * Reusable "review & edit extracted document data" grid.
 *
 * Usage:
 * <app-excel-editor
 *   [file]="myFile"
 *   [companyId]="companyId"
 *   [classId]="group_id"
 *   [modelType]="isSelectedDoc?.model_type"
 *   [documentTitle]="uploadFileForm.controls['title'].value"
 *   [(visible)]="showExcelEditor"
 *   (onSave)="handleSave($event)"
 *   (onCancel)="handleCancel()">
 * </app-excel-editor>
 *
 * Set `visible = true` to trigger the conversion + open the dialog.
 * The component calls elibraryService.uploadAndConvertFile(file, companyId)
 * itself the first time it becomes visible for a given file.
 */
@Component({
  selector: 'app-excel-editor',
  templateUrl: './exceleditor.component.html',
  styleUrls: ['./exceleditor.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    DialogModule,
    ButtonModule,
    TooltipModule,
    ProgressSpinnerModule
  ]
})
export class ExcelEditorComponent implements OnChanges {

  // =========================================================
  // INPUTS
  // =========================================================

  /** New upload mode */
  @Input() file: File | null = null;

  /** Existing document edit mode */
  @Input() documentId: string | number | null = null;

  /** Used in edit mode when file is not available */
  @Input() documentFileName = '';
  @Input() documentFileType = '';

  /** Document / training information */
  @Input() companyId = 0;
  @Input() classId = 0;
  @Input() modelType = 0;
  @Input() documentTitle = '';
  @Input() stateCode = '';
  @Input() llm_train_id = '';

  /** Dialog visibility */
  @Input() visible = false;

  // =========================================================
  // OUTPUTS
  // =========================================================

  @Output() visibleChange = new EventEmitter<boolean>();

  @Output() onSave = new EventEmitter<{
    payload: any[];
    response: any;
    isEdit?: boolean;
  }>();

  @Output() onCancel = new EventEmitter<void>();

  // =========================================================
  // TRAINING OPTIONS
  // =========================================================

  @Input() chatUserList: (string | number)[] = [];

  @Input() policyState = '';

  @Input() isDownload = false;

  @Input() docTrainId: string | number = '';

  @Input() messageId: string | number = '';

  // =========================================================
  // UI STATE
  // =========================================================

  loading = false;

  saving = false;

  errorMessage = '';

  // =========================================================
  // GRID
  // =========================================================

  columns: ExcelColumn[] = [
    {
      field: 'title',
      header: 'Title'
    },
    {
      field: 'content',
      header: 'Content'
    }
  ];

  rows: ExcelRow[] = [];

  // =========================================================
  // INTERNAL STATE
  // =========================================================

  /**
   * Keep original API response so additional fields
   * can be preserved if required later.
   */
  private rawResponse: any = null;

  /**
   * Prevent duplicate API calls when dialog is opened.
   */
  private hasLoaded = false;

  // =========================================================
  // SPECIAL ROW TITLES
  // =========================================================

  private static readonly HEADER_TITLE = 'Document Header';

  private static readonly FOOTER_TITLE = 'Document Footer';

  private static readonly RESOURCES_TITLE = 'Resources';

  // =========================================================
  // CONSTRUCTOR
  // =========================================================

  constructor(
    private elibraryService: ELibraryService,
    private alert: AlertPopup
  ) { }

  // =========================================================
  // INPUT CHANGES
  // =========================================================

  ngOnChanges(changes: SimpleChanges): void {

    const fileChanged = !!changes['file'];
    const documentChanged = !!changes['documentId'];

    const sourceChanged =
      (fileChanged && !changes['file'].firstChange) ||
      (documentChanged && !changes['documentId'].firstChange);

    // -------------------------------------------------------
    // Document/file changed
    // -------------------------------------------------------

    if (sourceChanged) {

      this.resetEditor();

      if (this.visible) {
        this.loadDocument();
      }

      return;
    }

    // -------------------------------------------------------
    // Dialog opened
    // -------------------------------------------------------

    if (
      changes['visible'] &&
      this.visible &&
      !this.hasLoaded &&
      !this.loading
    ) {
      this.loadDocument();
    }
  }

  // =========================================================
  // LOAD DOCUMENT
  // =========================================================

  loadDocument(): void {

    /**
     * documentId exists
     * -----------------
     * Existing document edit mode
     */
    if (this.documentId) {
      this.loadExistingDocument();
      return;
    }

    /**
     * No documentId
     * -------------
     * New upload mode
     */
    this.loadAndConvert();
  }

  // =========================================================
  // NEW UPLOAD
  // =========================================================

  private loadAndConvert(): void {

    // =====================================================
    // FILE VALIDATION
    // =====================================================

    if (!this.file) {

      this.loading = false;
      this.hasLoaded = false;

      this.errorMessage =
        'No file selected to convert.';

      return;
    }

    // =====================================================
    // START LOADING
    // =====================================================

    this.loading = true;
    this.hasLoaded = false;
    this.errorMessage = '';

    const companyId =
      localStorage.getItem('company_id') ||
      this.companyId;

    console.log('======================================');
    console.log('Uploading file:', this.file.name);
    console.log('Company ID:', companyId);
    console.log('======================================');

    // =====================================================
    // API
    // =====================================================

    this.elibraryService
      .processUploadedFile(
        this.file,
        companyId
      )
      .subscribe({

        // ===================================================
        // SUCCESS
        // ===================================================

        next: (res: any) => {

          console.log(
            'FULL UPLOAD RESPONSE:',
            res
          );

          this.rawResponse = res;

          // -----------------------------------------------
          // Find actual converted data
          // -----------------------------------------------

          let data = res?.data;

          console.log(
            'Initial response.data:',
            data
          );

          // -----------------------------------------------
          // Handle nested data
          // -----------------------------------------------

          if (
            data &&
            typeof data === 'object' &&
            data.data
          ) {
            data = data.data;

            console.log(
              'Nested response.data.data:',
              data
            );
          }

          // -----------------------------------------------
          // Handle JSON string
          // -----------------------------------------------

          if (typeof data === 'string') {

            try {

              data = JSON.parse(data);

              console.log(
                'Parsed JSON data:',
                data
              );

            } catch (error) {

              console.error(
                'JSON parsing failed:',
                error
              );

              this.loading = false;
              this.hasLoaded = false;

              this.errorMessage =
                'The converted document returned invalid JSON.';

              this.alert.showAlertMessage({
                message: this.errorMessage
              });

              return;
            }
          }

          // -----------------------------------------------
          // Validate data
          // -----------------------------------------------

          if (
            !data ||
            typeof data !== 'object'
          ) {

            console.error(
              'Invalid converted document:',
              data
            );

            this.loading = false;
            this.hasLoaded = false;

            this.errorMessage =
              'No valid document content was returned.';

            this.alert.showAlertMessage({
              message: this.errorMessage
            });

            return;
          }

          // -----------------------------------------------
          // Build Excel grid
          // -----------------------------------------------

          console.log(
            'Sending data to buildGrid:',
            data
          );

          this.buildGrid(data);

          console.log(
            'Generated Excel rows:',
            this.rows
          );

          // -----------------------------------------------
          // Make sure rows exist
          // -----------------------------------------------

          if (!this.rows.length) {

            console.warn(
              'buildGrid() generated zero rows'
            );

            this.errorMessage =
              'The document was converted, but no editable content was found.';

            this.loading = false;
            this.hasLoaded = false;

            return;
          }

          // -----------------------------------------------
          // Success
          // -----------------------------------------------

          this.loading = false;
          this.hasLoaded = true;
        },

        // ===================================================
        // ERROR
        // ===================================================

        error: (err: any) => {

          console.error(
            'Document conversion error:',
            err
          );

          this.loading = false;
          this.hasLoaded = false;

          this.errorMessage =
            err?.error?.message ||
            err?.message ||
            'Unable to convert the document. Please try again.';

          this.alert.showAlertMessage({
            message: this.errorMessage
          });
        }
      });
  }

  // =========================================================
  // EXISTING DOCUMENT
  // =========================================================

  private loadExistingDocument(): void {

    if (!this.documentId) {

      this.loading = false;

      this.errorMessage =
        'No document selected to edit.';

      return;
    }

    this.loading = true;

    this.errorMessage = '';

    this.elibraryService
      .getExtractedDocumentContent(this.documentId)
      .subscribe({

        next: (res: any) => {

          this.loading = false;

          this.hasLoaded = true;

          this.rawResponse = res;

          console.log(
            'Existing document response:',
            res?.data
          );

          this.buildGrid(
            res?.data ?? {}
          );
        },

        error: (err: any) => {

          this.loading = false;

          this.hasLoaded = false;

          this.errorMessage =
            err?.error?.message ||
            'Unable to load document content. Please try again.';

          this.alert.showAlertMessage({
            message: this.errorMessage
          });
        }
      });
  }

  // =========================================================
  // BUILD GRID
  // =========================================================

  // =========================================================
  // BUILD GRID
  // =========================================================

  private buildGrid(data: any): void {

    console.log('========== buildGrid START ==========');
    console.log('Raw data:', data);
    console.log('Data type:', typeof data);

    // =====================================================
    // PARSE JSON STRING
    // =====================================================

    if (typeof data === 'string') {

      try {

        data = JSON.parse(data);

        console.log('Parsed JSON:', data);

      } catch (error) {

        console.error(
          'Failed to parse document JSON:',
          error
        );

        this.rows = [];

        this.errorMessage =
          'Unable to parse converted document JSON.';

        return;
      }
    }

    // =====================================================
    // VALIDATE
    // =====================================================

    if (
      !data ||
      typeof data !== 'object' ||
      Array.isArray(data)
    ) {

      console.error(
        'Invalid document data:',
        data
      );

      this.rows = [];

      this.errorMessage =
        'No valid document data was returned.';

      return;
    }

    // =====================================================
    // TABLE
    // =====================================================

    const table: any[] =
      Array.isArray(data.table)
        ? data.table
        : [];

    // =====================================================
    // HEADER
    // =====================================================

    const header =
      this.valueToText(data.header);

    // =====================================================
    // FOOTER
    // =====================================================

    const footer =
      this.valueToText(data.footer);

    // =====================================================
    // RESOURCES
    // =====================================================

    let resources: ResourceItem[] = [];

    // -----------------------------------------------------
    // First get resources from data.resources
    // -----------------------------------------------------

    if (data.resources) {

      resources = [
        ...this.normalizeResources(
          data.resources
        )
      ];
    }

    // =====================================================
    // BUILD ROWS
    // =====================================================

    const rows: ExcelRow[] = [];

    let idCounter = 0;

    // =====================================================
    // HEADER
    // =====================================================

    rows.push({
      _id: idCounter++,
      _special: 'header',
      title: ExcelEditorComponent.HEADER_TITLE,
      content: header
    });

    // =====================================================
    // TABLE
    // =====================================================

    table.forEach((item: any) => {

      if (!item) {
        return;
      }

      // ===================================================
      // RESOURCES INSIDE TABLE
      // ===================================================

      if (
        item._special === 'resources'
      ) {

        const tableResources =
          this.normalizeResources(
            item.content
          );

        // -------------------------------------------------
        // Merge resources instead of creating another row
        // -------------------------------------------------

        resources = this.mergeResources(
          resources,
          tableResources
        );

        return;
      }

      // ===================================================
      // HEADER INSIDE TABLE
      // ===================================================

      if (
        item._special === 'header'
      ) {

        return;
      }

      // ===================================================
      // FOOTER INSIDE TABLE
      // ===================================================

      if (
        item._special === 'footer'
      ) {

        return;
      }

      // ===================================================
      // NORMAL TABLE CONTENT
      // ===================================================

      let content = '';

      if (
        Array.isArray(item.content)
      ) {

        content =
          item.content
            .map((value: any) =>
              this.valueToText(value)
            )
            .filter(Boolean)
            .join('\n');

      }
      else {

        content =
          this.valueToText(
            item.content
          );
      }

      // ===================================================
      // ADD NORMAL ROW
      // ===================================================

      rows.push({

        _id: idCounter++,

        title:
          String(
            item.title ?? ''
          ),

        content

      });

    });

    // =====================================================
    // FOOTER
    // =====================================================

    rows.push({

      _id: idCounter++,

      _special: 'footer',

      title:
        ExcelEditorComponent.FOOTER_TITLE,

      content: footer

    });

    // =====================================================
    // RESOURCES
    // =====================================================

    if (resources.length > 0) {

      rows.push({

        _id: idCounter++,

        _special: 'resources',

        title:
          ExcelEditorComponent.RESOURCES_TITLE,

        // IMPORTANT:
        // Keep resource objects.
        content: resources

      });

    }

    // =====================================================
    // ASSIGN
    // =====================================================

    this.rows = rows;

    // =====================================================
    // DEBUG
    // =====================================================

    console.log('Header:', header);

    console.log(
      'Table:',
      table
    );

    console.log(
      'Footer:',
      footer
    );

    console.log(
      'Resources:',
      resources
    );

    console.log(
      'Final rows:',
      this.rows
    );

    console.log(
      '========== buildGrid END =========='
    );
  }



  // =========================================================
  // MERGE RESOURCES
  // =========================================================

  private mergeResources(
    first: ResourceItem[],
    second: ResourceItem[]
  ): ResourceItem[] {

    return this.removeDuplicateResources([
      ...first,
      ...second
    ]);
  }


  // =========================================================
  // REMOVE DUPLICATE RESOURCES
  // =========================================================

  private removeDuplicateResources(
    resources: ResourceItem[]
  ): ResourceItem[] {

    const seen = new Set<string>();

    return resources.filter(
      (resource: ResourceItem) => {

        const key =
          `${resource.title}|${resource.url}`
            .toLowerCase()
            .trim();

        if (!key) {
          return false;
        }

        if (seen.has(key)) {
          return false;
        }

        seen.add(key);

        return true;
      }
    );
  }


  // =========================================================
  // EXTRACT URL
  // =========================================================

  private extractUrl(
    value: string
  ): string {

    if (!value) {
      return '';
    }

    const match =
      value.match(
        /https?:\/\/[^\s<>"')]+/i
      );

    return match
      ? match[0]
      : '';
  }


  // =========================================================
  // SAFE VALUE TO TEXT
  // =========================================================

  private valueToText(
    value: any
  ): string {

    if (
      value === null ||
      value === undefined
    ) {
      return '';
    }

    // -----------------------------------------------------
    // STRING
    // -----------------------------------------------------

    if (
      typeof value === 'string'
    ) {

      return value;
    }

    // -----------------------------------------------------
    // NUMBER / BOOLEAN
    // -----------------------------------------------------

    if (
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {

      return String(value);
    }

    // -----------------------------------------------------
    // ARRAY
    // -----------------------------------------------------

    if (
      Array.isArray(value)
    ) {

      return value
        .map(
          (item: any) =>
            this.valueToText(item)
        )
        .filter(Boolean)
        .join('\n');
    }

    // -----------------------------------------------------
    // OBJECT
    // -----------------------------------------------------

    if (
      typeof value === 'object'
    ) {

      if (
        value.text !== undefined
      ) {

        return this.valueToText(
          value.text
        );
      }

      if (
        value.content !== undefined
      ) {

        return this.valueToText(
          value.content
        );
      }

      if (
        value.title !== undefined
      ) {

        return this.valueToText(
          value.title
        );
      }

      try {

        return JSON.stringify(
          value
        );

      } catch {

        return '';
      }
    }

    return String(value);
  }



  // =========================================================
  // ADD ROW
  // =========================================================

  addRow(): void {

    const nextId =
      this.rows.reduce(
        (max, row) => Math.max(max, row._id),
        0
      ) + 1;

    const newRow: ExcelRow = {
      _id: nextId,
      title: '',
      content: ''
    };

    const insertAt = this.rows.findIndex(
      row =>
        row._special === 'footer' ||
        row._special === 'resources'
    );

    const index =
      insertAt === -1
        ? this.rows.length
        : insertAt;

    this.rows = [
      ...this.rows.slice(0, index),
      newRow,
      ...this.rows.slice(index)
    ];
  }

  // =========================================================
  // REMOVE ROW
  // =========================================================

  removeRow(index: number): void {

    const row = this.rows[index];

    /**
     * Header/footer/resources cannot be deleted.
     */
    if (row?._special) {
      return;
    }

    this.rows = this.rows.filter(
      (_, i) => i !== index
    );
  }

  // =========================================================
  // SPLIT GRID
  // =========================================================

  // =========================================================
  // SPLIT GRID
  // =========================================================

  private splitGrid(): {
    header: string;
    footer: string;
    resources: ResourceItem[];
    table: any[];
  } {

    let header = '';

    let footer = '';

    const resources: ResourceItem[] = [];

    const table: any[] = [];


    this.rows.forEach((row: ExcelRow) => {

      const content = row.content;


      // =====================================================
      // HEADER
      // =====================================================

      if (row._special === 'header') {

        header =
          this.valueToText(content);

        return;
      }


      // =====================================================
      // FOOTER
      // =====================================================

      if (row._special === 'footer') {

        footer =
          this.valueToText(content);

        return;
      }


      // =====================================================
      // RESOURCES
      // =====================================================

      if (row._special === 'resources') {

        const rowResources =
          this.normalizeResources(content);

        resources.push(
          ...rowResources
        );

        return;
      }


      // =====================================================
      // NORMAL TABLE ROW
      // =====================================================

      const contentStr =
        this.valueToText(content);


      const lines =
        contentStr
          .split(/\r?\n/)
          .map(
            (line: string) =>
              line.trim()
          )
          .filter(Boolean);


      table.push({

        title:
          String(
            row.title ?? ''
          ).trim(),

        content:
          lines.length > 1
            ? lines
            : contentStr

      });

    });


    // =====================================================
    // REMOVE DUPLICATE RESOURCES
    // =====================================================

    const uniqueResources =
      this.removeDuplicateResources(
        resources
      );


    // =====================================================
    // RESULT
    // =====================================================

    const result = {

      header,

      footer,

      resources:
        uniqueResources,

      table

    };


    console.log(
      '========== splitGrid =========='
    );

    console.log(
      'Header:',
      header
    );

    console.log(
      'Footer:',
      footer
    );

    console.log(
      'Resources:',
      uniqueResources
    );

    console.log(
      'Table:',
      table
    );

    console.log(
      '================================'
    );


    return result;
  }

  // =========================================================
  // GET FILE INFORMATION
  // =========================================================

  private getFileInfo(): {
    fileName: string;
    fileType: string;
  } {

    const sourceName =
      this.file?.name ||
      this.documentFileName ||
      this.rawResponse?.original_file ||
      '';

    /**
     * No filename available.
     */
    if (!sourceName) {

      return {
        fileName: '',
        fileType:
          this.documentFileType ||
          this.rawResponse?.file_type ||
          ''
      };
    }

    const lastDot =
      sourceName.lastIndexOf('.');

    /**
     * Filename without extension.
     */
    if (lastDot === -1) {

      return {
        fileName: sourceName,
        fileType:
          this.documentFileType ||
          this.rawResponse?.file_type ||
          ''
      };
    }

    return {

      fileName:
        sourceName.substring(
          0,
          lastDot
        ),

      fileType:
        this.documentFileType ||
        sourceName.substring(
          lastDot + 1
        )
    };
  }

  // =========================================================
  // SAVE
  // =========================================================

  save(): void {

    if (this.saving) {
      return;
    }

    if (this.loading) {
      return;
    }

    if (!this.rows.length) {

      this.alert.showAlertMessage({
        message:
          'There is no document content to save.'
      });

      return;
    }

    const {
      header,
      footer,
      resources,
      table
    } = this.splitGrid();

    /**
     * Existing document = edit mode.
     */
    const isEdit =
      !!this.documentId;

    const {
      fileName,
      fileType
    } = this.getFileInfo();

    const companyId =
      localStorage.getItem('company_id') ||
      this.companyId;

    // =======================================================
    // CONVERT JSON PAYLOAD
    // =======================================================

    const apiPayload: any = {

      company_id: companyId,

      activity: 'document',

      file_name: fileName,

      file_type: fileType,

      doc_train_id: this.llm_train_id,

      data: {
        header,
        table,
        footer,
        resources
      }
    };

    /**
     * Existing document:
     * tell backend which document must be updated.
     */
    if (isEdit) {

      apiPayload.message_id =
        this.documentId;
    }

    console.log(
      'Document save payload:',
      apiPayload
    );

    this.saving = true;

    // =======================================================
    // SAVE DOCUMENT
    // =======================================================

    this.elibraryService
      .convertJsonToDocument(apiPayload)
      .subscribe({

        next: (response: any) => {

          const doc_train_id = this.llm_train_id

          this.startDocumentTraining(
            response,
            table,
            fileName,
            isEdit,
            companyId
          );
        },

        error: (err: any) => {

          this.saving = false;

          const message =
            err?.error?.message ||
            'Unable to save the document. Please try again.';

          this.alert.showAlertMessage({
            message
          });
        }
      });
  }

  // =========================================================
  // DOCUMENT TRAINING
  // =========================================================

  private startDocumentTraining(
    response: any,
    table: any[],
    fileName: string,
    isEdit: boolean,
    companyId: string | number
  ): void {

    const trainPayload: any = {

      class_id: this.classId,

      company_id: Number(companyId),

      file_name:
        response?.file_name ||
        fileName,

      title:
        this.documentTitle ||
        fileName,

      model_type:
        this.modelType,

      document_type:
        'standard',

      mode:
        'assessment',

      authorize:
        localStorage.getItem('llm_token'),

      apiKey:
        'canstring@benefits-agent',

      chat_user_list:
        (this.chatUserList || []).join(','),

      uniq:
        String(Date.now()),

      policy_state:
        this.policyState || '',

      is_download:
        String(!!this.isDownload),

      doc_train_id:
        this.docTrainId || this.llm_train_id || '',

      document_id:
        '',

      stateCode:
        this.stateCode
    };

    /**
     * Existing document update.
     */
    if (isEdit) {

      trainPayload.message_id =
        this.documentId;
    }

    /**
     * If a message ID was supplied for a new document,
     * use it as fallback.
     */
    else if (this.messageId) {

      trainPayload.message_id =
        this.messageId;
    }

    console.log(
      'Document training payload:',
      trainPayload
    );

    // =====================================================
    // START TRAINING
    // =====================================================

    this.elibraryService
      .learnFromDocument(trainPayload)
      .subscribe({

        next: (trainResponse: any) => {

          this.saving = false;

          this.onSave.emit({

            payload: table,

            response: {
              ...response,
              trainResponse
            },

            isEdit
          });

          this.close();
        },

        error: (trainErr: any) => {

          this.saving = false;

          const message =
            trainErr?.error?.message ||
            'Document saved, but LLM training failed to start.';

          this.alert.showAlertMessage({
            message
          });

          /**
           * Document itself was successfully saved,
           * therefore notify parent even if training failed.
           */
          this.onSave.emit({

            payload: table,

            response,

            isEdit
          });

          this.close();
        }
      });
  }

  // =========================================================
  // CANCEL
  // =========================================================

  cancel(): void {

    this.onCancel.emit();

    this.close();
  }

  // =========================================================
  // CLOSE
  // =========================================================

  private close(): void {

    this.visible = false;

    this.visibleChange.emit(false);

    /**
     * Do NOT reset documentId here.
     *
     * documentId is an @Input() controlled by the parent.
     * The parent decides whether the next operation is
     * upload mode or edit mode.
     */
  }

  // =========================================================
  // RESET EDITOR
  // =========================================================

  private resetEditor(): void {

    this.loading = false;

    this.saving = false;

    this.errorMessage = '';

    this.hasLoaded = false;

    this.rows = [];

    this.rawResponse = null;
  }

  // =========================================================
  // EXCEL EXPORT
  // =========================================================

  exportToExcel(): void {

    this.alert.showAlertMessage({

      message:
        'Install the "xlsx" package and uncomment exportToExcel() to enable .xlsx export.'
    });
  }

  // =========================================================
  // STOP TABLE CELL NAVIGATION
  // =========================================================

  stopCellNav(event: KeyboardEvent): void {

    const navKeys = [
      'ArrowUp',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight'
    ];

    if (navKeys.includes(event.key)) {

      event.stopPropagation();
    }
  }

  isResourceArray(value: any): boolean {
    return Array.isArray(value) && value.length > 0;
  }

  getResources(value: any): ResourceItem[] {
    return this.normalizeResources(value);
  }

  private normalizeResources(value: any): ResourceItem[] {

    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return [];
    }


    // =========================================================
    // JSON STRING
    // =========================================================

    if (typeof value === 'string') {

      const trimmed = value.trim();

      if (!trimmed) {
        return [];
      }

      // Try JSON first
      if (
        trimmed.startsWith('[') ||
        trimmed.startsWith('{')
      ) {

        try {

          const parsed = JSON.parse(trimmed);

          return this.normalizeResources(parsed);

        } catch (error) {

          console.warn(
            'Unable to parse resources JSON:',
            value
          );

        }

      }


      // Normal text / URL lines
      return this.removeDuplicateResources(
        trimmed
          .split(/\r?\n/)
          .map((item: string) => item.trim())
          .filter(Boolean)
          .map((item: string) => {

            const url =
              this.extractUrl(item);

            return {
              title:
                url && item === url
                  ? url
                  : item,
              url
            };

          })
      );
    }


    // =========================================================
    // ARRAY
    // =========================================================

    if (Array.isArray(value)) {

      const result: ResourceItem[] = [];

      value.forEach((resource: any) => {

        if (
          resource === null ||
          resource === undefined
        ) {
          return;
        }


        // Resource object
        if (
          typeof resource === 'object' &&
          !Array.isArray(resource)
        ) {

          const title =
            String(
              resource.title ??
              resource.name ??
              resource.label ??
              resource.text ??
              ''
            ).trim();

          const url =
            String(
              resource.url ??
              resource.href ??
              resource.link ??
              ''
            ).trim();

          if (title || url) {

            result.push({
              title: title || url,
              url
            });

          }

          return;
        }


        // String resource
        const text =
          String(resource).trim();

        if (!text) {
          return;
        }

        const url =
          this.extractUrl(text);

        result.push({
          title:
            url && text === url
              ? url
              : text,
          url
        });

      });


      return this.removeDuplicateResources(result);
    }


    // =========================================================
    // SINGLE OBJECT
    // =========================================================

    if (typeof value === 'object') {

      const title =
        String(
          value.title ??
          value.name ??
          value.label ??
          value.text ??
          ''
        ).trim();

      const url =
        String(
          value.url ??
          value.href ??
          value.link ??
          ''
        ).trim();

      if (title || url) {

        return [{
          title: title || url,
          url
        }];

      }

    }


    return [];
  }
}
